import { useEffect, useMemo, useState } from 'react';
import { api } from '../api/client';
import { confirmDialog } from '../utils/confirm';
import { formatXof } from '../utils/format';

// Page CRUD générique pilotée par une config de registre.
export default function RegistryPage({ config, basePath = '/registry' }) {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');
  const [modal, setModal] = useState(null);
  const [view, setView] = useState(null);
  const [refs, setRefs] = useState({ classes: [], students: [] });

  const fieldsByKey = useMemo(() => Object.fromEntries(config.fields.map((f) => [f.key, f])), [config]);

  function load() {
    api.get(`${basePath}/${config.key}${search.trim() ? `?search=${encodeURIComponent(search.trim())}` : ''}`)
      .then(setRows).catch((e) => setError(e.message));
  }

  useEffect(() => {
    setSearch(''); setRows([]); setError('');
    // Charger les listes de référence utilisées par la config.
    const needClasses = config.fields.some((f) => f.ref === 'classes');
    const needStudents = config.fields.some((f) => f.ref === 'students');
    if (needClasses) api.get('/catalog/classes').then((c) => setRefs((r) => ({ ...r, classes: c.map((x) => x.name) }))).catch(() => {});
    if (needStudents) api.get('/students').then((s) => setRefs((r) => ({ ...r, students: s.map((x) => `${x.lastName} ${x.firstName}`) }))).catch(() => {});
  }, [config.key]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [config.key, search]);

  async function archive(id) {
    if (!(await confirmDialog({ title: 'Archiver', message: 'Archiver cet enregistrement ?', confirmLabel: 'Archiver', danger: true }))) return;
    try { await api.del(`${basePath}/${config.key}/${id}`); load(); } catch (e) { setError(e.message); }
  }

  function renderCell(rec, col) {
    const f = fieldsByKey[col];
    const v = rec.data?.[col];
    if (v == null || v === '') return '—';
    if (f?.type === 'date') return new Date(v).toLocaleDateString('fr-FR');
    if (f?.type === 'money') return formatXof(Number(v));
    return String(v);
  }

  return (
    <div className="panel">
      <div className="panel-head">
        <h2>{config.title}</h2>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="search-input" style={{ width: 220 }} placeholder="Rechercher…" value={search} onChange={(e) => setSearch(e.target.value)} />
          <button className="btn btn-sm" onClick={() => setModal({})}>+ Ajouter</button>
        </div>
      </div>
      <div className="panel-body">
        {error && <div className="form-error">{error}</div>}
        {rows.length === 0 ? <div className="empty">Aucun enregistrement.</div> : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {config.columns.map((c) => <th key={c}>{fieldsByKey[c]?.label || c}</th>)}
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((rec) => (
                  <tr key={rec.id}>
                    {config.columns.map((c) => <td key={c}>{renderCell(rec, c)}</td>)}
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button className="btn-ghost btn-sm" onClick={() => setView(rec)}>Vue</button>
                      <button className="btn-ghost btn-sm" onClick={() => setModal(rec)}>Modifier</button>
                      <button className="link-danger" onClick={() => archive(rec.id)}>Archiver</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <RecordModal config={config} refs={refs} basePath={basePath} record={modal}
          onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />
      )}
      {view && (
        <RecordView config={config} record={view}
          onClose={() => setView(null)} onEdit={() => { setModal(view); setView(null); }} />
      )}
    </div>
  );
}

function RecordModal({ config, refs, basePath = '/registry', record, onClose, onSaved }) {
  const isEdit = !!record.id;
  const [form, setForm] = useState(() => {
    const base = {};
    config.fields.forEach((f) => { base[f.key] = record.data?.[f.key] ?? ''; });
    return base;
  });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [photo, setPhoto] = useState(record.data?.photo || '');
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  function onPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { setError('Photo trop lourde (max 2 Mo).'); return; }
    const reader = new FileReader();
    reader.onload = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  }

  async function submit(e) {
    e.preventDefault(); setError(''); setSaving(true);
    const data = {};
    config.fields.forEach((f) => {
      let v = form[f.key];
      if (v === '') return;
      if (f.type === 'number' || f.type === 'money') v = Number(v);
      data[f.key] = v;
    });
    if (config.photo && photo) data.photo = photo;
    const searchText = config.fields
      .map((f) => form[f.key]).filter((v) => typeof v === 'string' && v).join(' ');
    const payload = {
      data,
      label: form[config.labelField] || undefined,
      searchText,
      refDate: (config.dateField && form[config.dateField]) || undefined,
    };
    try {
      if (isEdit) await api.patch(`${basePath}/${config.key}/${record.id}`, payload);
      else await api.post(`${basePath}/${config.key}`, payload);
      onSaved();
    } catch (err) { setError(err.message); setSaving(false); }
  }

  function renderField(f) {
    const common = { value: form[f.key], onChange: set(f.key), required: f.required };
    if (f.type === 'textarea') return <textarea rows="2" {...common} />;
    if (f.type === 'select') return (
      <select {...common}><option value="">—</option>{f.options.map((o) => <option key={o} value={o}>{o}</option>)}</select>
    );
    if (f.type === 'ref') return (
      <select {...common}><option value="">—</option>{(refs[f.ref] || []).map((o) => <option key={o} value={o}>{o}</option>)}</select>
    );
    if (f.type === 'number' || f.type === 'money') return <input type="number" {...common} />;
    if (f.type === 'date') return <input type="date" {...common} />;
    return <input type="text" {...common} />;
  }

  return (
    <div className="modal-overlay">
      <div className={`modal ${config.photo || config.fields.length > 18 ? 'modal-xl' : 'modal-wide'}`} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 16 }}>
          {isEdit ? 'Modifier' : 'Nouveau'} — {config.title}
        </h2>
        {error && <div className="form-error">{error}</div>}
        <form onSubmit={submit}>
          {config.photo && (
            <div className="reg-photo">
              <div className="reg-photo-preview">
                {photo ? <img src={photo} alt="photo" /> : <span>Aucune photo</span>}
              </div>
              <div>
                <label className="btn-ghost btn-sm" style={{ cursor: 'pointer', display: 'inline-block' }}>
                  {photo ? 'Changer la photo' : 'Ajouter une photo'}
                  <input type="file" accept="image/*" onChange={onPhoto} style={{ display: 'none' }} />
                </label>
                {photo && <button type="button" className="link-danger" onClick={() => setPhoto('')} style={{ marginLeft: 8 }}>Retirer</button>}
                <p style={{ color: 'var(--muted)', fontSize: '0.78rem', marginTop: 6 }}>JPG ou PNG, max 2 Mo.</p>
              </div>
            </div>
          )}
          <div className="grid-3">
            {config.fields.map((f) => (
              <div className="field" key={f.key} style={f.type === 'textarea' ? { gridColumn: 'span 3' } : undefined}>
                <label>{f.label}{f.required ? ' *' : ''}</label>
                {renderField(f)}
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
            <button className="btn" type="submit" disabled={saving} style={{ width: 'auto', padding: '0 22px' }}>
              {saving ? 'Enregistrement…' : (isEdit ? 'Enregistrer' : 'Créer')}
            </button>
            <button type="button" className="btn-ghost" onClick={onClose}>Annuler</button>
          </div>
        </form>
      </div>
    </div>
  );
}


function RecordView({ config, record, onClose, onEdit }) {
  const money = (v) => (v != null && v !== '' ? formatXof(Number(v)) : '—');
  function display(f) {
    const v = record.data?.[f.key];
    if (v == null || v === '') return '—';
    if (f.type === 'date') return new Date(v).toLocaleDateString('fr-FR');
    if (f.type === 'money') return money(v);
    return String(v);
  }
  return (
    <div className="modal-overlay">
      <div className={`modal ${config.photo || config.fields.length > 18 ? 'modal-xl' : 'modal-wide'}`} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ fontFamily: 'Space Grotesk, sans-serif', marginBottom: 16 }}>{config.title} — détails</h2>
        {config.photo && record.data?.photo && (
          <img src={record.data.photo} alt="" style={{ width: 84, height: 96, borderRadius: 8, objectFit: 'cover', border: '1px solid var(--line)', marginBottom: 12 }} />
        )}
        <div className="view-grid">
          {config.fields.filter((f) => f.type !== 'textarea').map((f) => (
            <div className="view-row" key={f.key}><span className="view-label">{f.label}</span><span className="view-value">{display(f)}</span></div>
          ))}
        </div>
        {config.fields.filter((f) => f.type === 'textarea' && record.data?.[f.key]).map((f) => (
          <div key={f.key} style={{ marginTop: 10 }}>
            <div className="view-label" style={{ marginBottom: 2 }}>{f.label}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{record.data[f.key]}</div>
          </div>
        ))}
        <div style={{ display: 'flex', gap: 10, marginTop: 18 }}>
          <button className="btn" onClick={onEdit} style={{ width: 'auto', padding: '0 22px' }}>Modifier</button>
          <button type="button" className="btn-ghost" onClick={onClose}>Fermer</button>
        </div>
      </div>
    </div>
  );
}
